import React from "react";
import { Box, Skeleton, useTheme, useMediaQuery } from "@mui/material";
import { ViewTickerMobileSkeleton } from "./ViewTickerMobileSkeleton";

/**
 * Deliberately minimal (Ben, 2026-08-01): no outlines for tabs, range chips,
 * buttons or the search bar — just the header text and the outlined cards
 * that hold the charts, each with a single pulsing fill. Default MUI pulse
 * and theme colours, matching every other skeleton in the app.
 */
export const ViewTickerSkeleton: React.FC = () => {
  const theme = useTheme();
  // Match the custom breakpoint used by ViewTicker itself so the skeleton
  // flips to mobile at the same width as the real page.
  const isMobile = useMediaQuery("(max-width: 1080px)");
  const isDark = theme.palette.mode === "dark";

  if (isMobile) {
    return <ViewTickerMobileSkeleton />;
  }

  // Flat UI color system matching ViewTicker
  const flatUIColors = {
    background: isDark ? "#1a1a1a" : "#ffffff",
    surface: isDark ? "#212121" : "#f5f5f5",
    border: isDark ? "rgba(255,255,255,0.1)" : "#e2e8f0",
  };

  const cardSx = {
    backgroundColor: flatUIColors.surface,
    border: `1px solid ${flatUIColors.border}`,
  };

  // MUI Skeleton's root defaults to a 1.2em height, so full-card fills need
  // the explicit override to stretch inside their flex card.
  const fillSx = { flex: 1, height: "auto", borderRadius: 1 };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        backgroundColor: flatUIColors.background,
        // MUI's default pulse waits 0.5s before its first cycle and fades
        // over 2s — on a fast load that reads as a static grey block. Faster
        // cycle, no delay, deeper dip so the pulse is unmistakable.
        "@keyframes tickerSkeletonPulse": {
          "0%": { opacity: 1 },
          "50%": { opacity: 0.35 },
          "100%": { opacity: 1 },
        },
        "& .MuiSkeleton-root": {
          animation: "tickerSkeletonPulse 1.1s ease-in-out infinite",
        },
      }}
    >
      {/* Desktop Header — company identity and price only */}
      <Box
        sx={{
          height: 64,
          display: "flex",
          alignItems: "center",
          px: 3,
          gap: 2,
          borderBottom: `1px solid ${flatUIColors.border}`,
          backgroundColor: flatUIColors.background,
        }}
      >
        {/* Logo */}
        <Skeleton variant="rounded" width={40} height={40} sx={{ borderRadius: 1 }} />

        {/* Company Name and Symbol */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Skeleton variant="text" width={150} height={20} />
          <Skeleton variant="text" width={110} height={16} />
        </Box>

        {/* Price Section */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, ml: 2 }}>
          <Skeleton variant="text" width={80} height={28} />
          <Skeleton variant="text" width={70} height={18} />
        </Box>
      </Box>

      {/* Tab Content with Full Width */}
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          px: 3,
          pt: 2,
          pb: 3,
          backgroundColor: flatUIColors.background,
        }}
      >
        {/* Company Info and Price Chart Row */}
        <Box sx={{ display: "flex", gap: 2 }}>
          {/* Company Profile Card - Left Side (40%) */}
          <Box
            sx={{
              ...cardSx,
              flex: "0 0 40%",
              borderRadius: 3,
              p: 2,
              display: "flex",
            }}
          >
            <Skeleton variant="rounded" sx={fillSx} />
          </Box>

          {/* Charts Column - Intraday Price and Volume (60%) */}
          <Box
            sx={{
              flex: "1 1 60%",
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              gap: 2,
              minHeight: "80vh",
            }}
          >
            {/* Intraday Price Chart Card */}
            <Box
              sx={{
                ...cardSx,
                borderRadius: 3,
                p: 1.5,
                display: "flex",
                flex: "1 1 50%",
                minHeight: "40vh",
              }}
            >
              <Skeleton variant="rounded" sx={fillSx} />
            </Box>

            {/* Volume Chart Card */}
            <Box
              sx={{
                ...cardSx,
                borderRadius: 3,
                p: 1.5,
                display: "flex",
                flex: "1 1 50%",
                minHeight: "40vh",
              }}
            >
              <Skeleton variant="rounded" sx={fillSx} />
            </Box>
          </Box>
        </Box>

        {/* Recent Developments Card - Full Width */}
        <Box sx={{ ...cardSx, borderRadius: 3, p: 3, mt: 2, height: 240, display: "flex" }}>
          <Skeleton variant="rounded" sx={fillSx} />
        </Box>

        {/* Market Cap Chart Card - Full Width */}
        <Box sx={{ ...cardSx, borderRadius: 2, p: 1.5, mt: 2, height: 340, display: "flex" }}>
          <Skeleton variant="rounded" sx={fillSx} />
        </Box>
      </Box>
    </Box>
  );
};
